import React from "react";

/**
 * Barre d'onglets partagée, déclinée par niveau de hiérarchie.
 *
 * Les trois niveaux d'onglets de la page hôtel partageaient exactement le même style
 * (soulignement bleu, `text-sm`), ce qui rendait la position dans la hiérarchie illisible.
 * Chaque niveau a désormais sa forme propre :
 *
 * - `primary`   (N1 : Général / LuniShower / GreenMiles / …) — soulignement vert marque,
 *   plus gros et plus gras : c'est la navigation de la page.
 * - `secondary` (N2 : sous-onglets GreenMiles et LuniShower) — segmented control gris,
 *   visuellement subordonné.
 *
 * Le N3 (types de mail) garde ses pastilles vertes dans une carte titrée : déjà distinct.
 */

const VARIANTS = {
  primary: {
    wrapper: "border-b border-gray-200",
    nav: "-mb-px flex space-x-8 overflow-x-auto",
    tab: "py-2.5 px-1 border-b-2 text-base font-semibold whitespace-nowrap transition-colors duration-200",
    active: "border-[#365644] text-[#365644]",
    inactive: "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300",
  },
  secondary: {
    wrapper: "mb-6 overflow-x-auto",
    nav: "inline-flex gap-1 rounded-lg bg-gray-100 p-1",
    tab: "px-3 py-1.5 rounded-md text-sm font-medium whitespace-nowrap transition-colors duration-200",
    active: "bg-white text-gray-900 shadow-sm",
    inactive: "text-gray-600 hover:text-gray-900 hover:bg-gray-200/60",
  },
};

/**
 * @param {object}   props
 * @param {Array}    props.tabs            [{ value, label }]
 * @param {string}   props.value           Onglet actif
 * @param {Function} props.onChange        (value) => void
 * @param {string}   props.variant         "primary" | "secondary"
 * @param {string[]} props.disabledValues  Onglets non encore cliquables (données en vol)
 * @param {Function} props.onTabHover      (value) => void — préchargement du chunk au survol
 */
const Tabs = ({ tabs = [], value, onChange, variant = "primary", disabledValues = [], onTabHover }) => {
  const styles = VARIANTS[variant] || VARIANTS.primary;

  return (
    <div className={styles.wrapper}>
      <nav className={styles.nav} role="tablist">
        {tabs.map((tab) => {
          const isActive = value === tab.value;
          const isDisabled = disabledValues.includes(tab.value);

          return (
            <button
              key={tab.value}
              type="button"
              role="tab"
              aria-selected={isActive}
              disabled={isDisabled}
              onClick={() => onChange(tab.value)}
              // Le survol précharge le chunk avant même le clic : pas de `pointer-events-none`
              // sur les onglets désactivés, pour que le préchargement reste possible.
              onMouseEnter={() => onTabHover?.(tab.value)}
              onFocus={() => onTabHover?.(tab.value)}
              className={`
                ${styles.tab}
                ${isActive ? styles.active : styles.inactive}
                ${isDisabled ? "opacity-50 cursor-wait" : ""}
              `}
            >
              {tab.label}
            </button>
          );
        })}
      </nav>
    </div>
  );
};

export default Tabs;
